import { addPasskey } from "./auth";
import { $, copyText, el, on } from "./dom";

const ICON_PATHS: Record<string, string> = {
  copy: '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>',
  trash:
    '<path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-9 0 1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13"/>',
};

/** Build an icon SVG matching the server <Icon> markup (so client-added rows look identical). */
function iconEl(name: keyof typeof ICON_PATHS): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "icon icon-sm");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.8");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = ICON_PATHS[name] ?? "";
  return svg;
}

interface NewInvite {
  code: string;
  note?: string | null;
  expiresAt?: string | null;
}

/** Build an unused-invite <li> identical to the server-rendered row. */
function buildInviteRow(inv: NewInvite): HTMLElement {
  const status = inv.expiresAt ? `Expires ${inv.expiresAt.slice(0, 10)}` : "Unused";
  const meta = status + (inv.note ? ` · ${inv.note}` : "");
  const copy = el("button", {
    class: "btn btn-icon",
    "data-action": "copy-invite",
    type: "button",
    "aria-label": "Copy code",
  });
  copy.append(iconEl("copy"));
  const revoke = el("button", {
    class: "btn btn-icon btn-danger-ghost",
    "data-action": "revoke-invite",
    type: "button",
    "aria-label": "Revoke invite",
  });
  revoke.append(iconEl("trash"));
  return el("li", { class: "invite", "data-code": inv.code }, [
    el("code", { class: "invite-code" }, [inv.code]),
    el("span", { class: "invite-meta muted" }, [meta]),
    copy,
    revoke,
  ]);
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function patchJson(url: string, body: unknown, method = "PATCH"): Promise<boolean> {
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.ok;
}

/** Header account dropdown: toggle on the avatar, close on outside-click / Escape. */
export function initAccountMenu(): void {
  const trigger = $("#account-trigger");
  const dropdown = $("#account-dropdown");
  if (!trigger || !dropdown) return;

  const isOpen = () => !dropdown.hasAttribute("hidden");
  const open = () => {
    dropdown.removeAttribute("hidden");
    trigger.setAttribute("aria-expanded", "true");
  };
  const close = () => {
    dropdown.setAttribute("hidden", "");
    trigger.setAttribute("aria-expanded", "false");
  };

  on(trigger, "click", (e) => {
    e.stopPropagation();
    isOpen() ? close() : open();
  });
  on(document, "click", (e) => {
    if (isOpen() && !(e.target as Element)?.closest("#account-menu")) close();
  });
  on(document, "keydown", (e) => {
    if ((e as KeyboardEvent).key === "Escape" && isOpen()) {
      close();
      (trigger as HTMLElement).focus();
    }
  });
}

/** Account settings page: profile/Gravatar, passkey devices, delete account. */
export function initSettings(): void {
  const root = $("#settings");
  if (!root) return;

  const displayName = $<HTMLInputElement>("#set-displayname");
  const gravatar = $<HTMLInputElement>("#set-gravatar");
  const preview = $("#avatar-preview");
  const status = $("#settings-status");
  const deviceStatus = $("#device-status");
  const setStatus = (t: string) => {
    if (status) status.textContent = t;
  };

  // Live Gravatar preview while typing an email.
  let previewTimer = 0;
  on(gravatar, "input", () => {
    window.clearTimeout(previewTimer);
    previewTimer = window.setTimeout(async () => {
      const email = gravatar?.value.trim().toLowerCase();
      if (!email || !preview) return;
      const hash = await sha256Hex(email);
      const img = el("img", {
        class: "avatar",
        width: "56",
        height: "56",
        src: `https://www.gravatar.com/avatar/${hash}?s=112&d=identicon`,
        alt: "",
      });
      preview.replaceChildren(img);
    }, 350);
  });

  on($("#settings-save"), "click", async () => {
    setStatus("Saving…");
    const body: Record<string, unknown> = {};
    if (displayName) body.displayName = displayName.value.trim();
    if (gravatar?.value.trim()) body.gravatarEmail = gravatar.value.trim();
    const ok = await patchJson("/api/account", body);
    if (ok) window.location.reload();
    else setStatus("Couldn't save. Try again.");
  });

  on($("#settings-clear-avatar"), "click", async () => {
    setStatus("Removing avatar…");
    const ok = await patchJson("/api/account", { gravatarEmail: null });
    if (ok) window.location.reload();
    else setStatus("Couldn't update.");
  });

  // Device list: rename / remove (delegated).
  on($("#device-list"), "click", async (e) => {
    const action = (e.target as HTMLElement)?.closest<HTMLElement>("[data-action]")?.dataset.action;
    const li = (e.target as HTMLElement)?.closest<HTMLElement>(".device");
    const id = li?.dataset.id;
    if (!id || !li) return;
    if (action === "rename-device") {
      const current = li.querySelector(".device-name")?.textContent ?? "";
      const name = window.prompt("Device name", current);
      if (name == null) return;
      if (await patchJson(`/api/account/credentials/${id}`, { name })) {
        const el2 = li.querySelector(".device-name");
        if (el2) el2.textContent = name || "Passkey";
      }
    } else if (action === "remove-device") {
      if (!window.confirm("Remove this passkey?")) return;
      const res = await fetch(`/api/account/credentials/${id}`, { method: "DELETE" });
      if (res.ok) li.remove();
      else if (deviceStatus)
        deviceStatus.textContent = (await res.json()).error ?? "Couldn't remove.";
    }
  });

  on($("#add-passkey"), "click", async () => {
    if (deviceStatus) deviceStatus.textContent = "Follow your browser's passkey prompt…";
    const result = await addPasskey();
    if (result.ok) window.location.reload();
    else if (deviceStatus) deviceStatus.textContent = result.error ?? "Couldn't add passkey.";
  });

  on($("#delete-account"), "click", async () => {
    if (!window.confirm("Delete your account and ALL your pastes? This cannot be undone.")) return;
    const res = await fetch("/api/account", { method: "DELETE" });
    if (res.ok) window.location.href = "/";
  });

  // Invites (admin only; these elements are absent for non-admins).
  const inviteList = $("#invite-list");
  const inviteStatus = $("#invite-status");
  const setInviteStatus = (t: string) => {
    if (inviteStatus) inviteStatus.textContent = t;
  };

  on($("#invite-generate"), "click", async () => {
    setInviteStatus("Generating…");
    const note = $<HTMLInputElement>("#invite-note")?.value.trim() || null;
    const days = Number($<HTMLSelectElement>("#invite-expiry")?.value || "0");
    const res = await fetch("/api/invites", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note, expiresInDays: days }),
    });
    if (!res.ok) {
      setInviteStatus("Couldn't generate a code.");
      return;
    }
    const { invite } = (await res.json()) as { invite: NewInvite };
    root.querySelector(".invite-empty")?.remove();
    inviteList?.prepend(buildInviteRow(invite));
    const noteInput = $<HTMLInputElement>("#invite-note");
    if (noteInput) noteInput.value = "";
    const copied = await copyText(invite.code);
    setInviteStatus(
      copied ? `Created ${invite.code} — copied to clipboard.` : `Created ${invite.code}.`,
    );
  });

  on(inviteList, "click", async (e) => {
    const action = (e.target as HTMLElement)?.closest<HTMLElement>("[data-action]")?.dataset.action;
    const li = (e.target as HTMLElement)?.closest<HTMLElement>(".invite");
    const code = li?.dataset.code;
    if (!code || !li) return;
    if (action === "copy-invite") {
      setInviteStatus((await copyText(code)) ? `Copied ${code}.` : code);
    } else if (action === "revoke-invite") {
      if (!window.confirm(`Revoke ${code}? It will stop working immediately.`)) return;
      const res = await fetch(`/api/invites/${encodeURIComponent(code)}`, { method: "DELETE" });
      if (res.ok) li.remove();
      else setInviteStatus("Couldn't revoke that code.");
    }
  });
}
